// src/components/ContactForm.jsx

import React, { useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import api from "../config/api";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (!data) return "Something went wrong. Please try again or contact us directly.";

    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      const val = data[firstKey];
      if (Array.isArray(val) && val.length) return val[0];
      if (typeof val === "string") return val;
    }

    return "Something went wrong. Please try again or contact us directly.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      await api.post("contact/", formData);

      setSuccessMsg("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (error) {
      console.error("Backend error response:", error.response?.data);
      setErrorMsg(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 bg-gradient-to-br from-accent via-primary/20 to-primary/10 text-secondary"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-xs font-medium border border-secondary">
            Get In Touch
          </span>
          <h2 className="text-3xl font-bold mt-3">Contact Us</h2>
          <p className="mt-2 text-secondary max-w-2xl mx-auto">
            Ready to start your project? Let's discuss how we can help bring
            your vision to life.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-secondary rounded-2xl text-accent p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-6">Let's Connect</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary text-secondary flex items-center justify-center shrink-0">
                    <EmailIcon />
                  </div>
                  <div>
                    <h4 className="font-medium">Email</h4>
                    <p className="">hello@mockingjaytech.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary text-secondary flex items-center justify-center shrink-0">
                    <PhoneIcon />
                  </div>
                  <div>
                    <h4 className="font-medium">Phone</h4>
                    <p className="">+91 7385330711</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary text-secondary flex items-center justify-center shrink-0">
                    <LocationOnIcon />
                  </div>
                  <div>
                    <h4 className="font-medium">Location</h4>
                    <p className="">A/p Islampur, Maharashtra, India</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-primary">
                <h4 className="font-medium mb-3">Response Time</h4>
                <p className="text-sm">
                  We typically respond within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-secondary rounded-2xl text-accent p-6 shadow-sm">
              {successMsg && (
                <div className="mb-6 p-4 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary">
                  {successMsg}
                </div>
              )}
              {errorMsg && (
                <div className="mb-6 p-4 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary">
                  {errorMsg}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Enter your company name"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your phone number"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Project Details *
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us about your project, requirements, timeline, and budget..."
                    className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto px-8 py-3 rounded-lg bg-primary text-secondary font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <SendIcon fontSize="small" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
